import * as BrowserSentry from "@sentry/browser";
import * as Sentry from "@sentry/vue";
import type { Pinia } from "pinia";
import type { App } from "vue";
import type { Router } from "vue-router";

const DEFAULT_ENVIRONMENT = "production";

type RuntimeSentryConfig = {
	dsn?: string | null;
	enabled?: boolean;
	environment?: string;
	release?: string;
};

type InitializedSentryState = {
	browserTracingAttached: boolean;
	enabled: boolean;
	environment: string;
	feedbackAttached: boolean;
	piniaAttached: boolean;
	vueAttached: boolean;
};

const resolveEnv = () =>
	typeof import.meta !== "undefined" ? import.meta.env : undefined;

const resolveRuntimeSentryConfig = (): RuntimeSentryConfig | undefined => {
	if (typeof window === "undefined") return undefined;

	return window.__BOW_RUNTIME_CONFIG__?.sentry;
};

const normalizeBoolean = (value?: string): boolean | undefined => {
	if (!value) return undefined;

	const normalized = value.trim().toLowerCase();
	if (normalized === "true") return true;
	if (normalized === "false") return false;

	return undefined;
};

const resolveEnvironment = (runtimeConfig?: RuntimeSentryConfig) => {
	const env = resolveEnv();
	const environmentOverride =
		runtimeConfig?.environment ?? env?.VITE_SENTRY_ENVIRONMENT;
	const mode = env?.MODE ?? process.env.NODE_ENV ?? DEFAULT_ENVIRONMENT;
	const environment = (
		environmentOverride ??
		mode ??
		DEFAULT_ENVIRONMENT
	).trim();

	return environment.length > 0 ? environment : DEFAULT_ENVIRONMENT;
};

const resolveDsn = (runtimeConfig?: RuntimeSentryConfig) => {
	const runtimeDsn = runtimeConfig?.dsn;
	if (runtimeDsn === null) return undefined;
	if (typeof runtimeDsn === "string") {
		const trimmed = runtimeDsn.trim();
		return trimmed.length > 0 ? trimmed : undefined;
	}

	const envDsn = resolveEnv()?.VITE_SENTRY_DSN;
	if (typeof envDsn === "string") {
		const trimmed = envDsn.trim();
		return trimmed.length > 0 ? trimmed : undefined;
	}

	return undefined;
};

const resolveRelease = (runtimeConfig?: RuntimeSentryConfig) => {
	const release = runtimeConfig?.release ?? resolveEnv()?.VITE_SENTRY_RELEASE;
	if (typeof release !== "string") return undefined;

	const trimmed = release.trim();
	return trimmed.length > 0 ? trimmed : undefined;
};

const resolveEnabled = (
	environment: string,
	dsn: string | undefined,
	runtimeConfig?: RuntimeSentryConfig,
) => {
	const env = resolveEnv();
	const enabledOverride =
		runtimeConfig?.enabled ?? normalizeBoolean(env?.VITE_SENTRY_ENABLED);
	const shouldEnable = enabledOverride ?? environment === "production";

	return shouldEnable && Boolean(dsn);
};

let sentryState: InitializedSentryState | null = null;

const initializeSentryBase = () => {
	if (sentryState) {
		return sentryState;
	}

	const runtimeConfig = resolveRuntimeSentryConfig();
	const environment = resolveEnvironment(runtimeConfig);
	const dsn = resolveDsn(runtimeConfig);
	const release = resolveRelease(runtimeConfig);
	const enabled = resolveEnabled(environment, dsn, runtimeConfig);

	BrowserSentry.init({
		dsn,
		enabled,
		environment,
		release,
		sendDefaultPii: enabled,
		tracesSampleRate: 0.1,
		tracePropagationTargets: [
			"https://bosun.ai",
			"https://api.bosun.ai",
			/^\/api\//,
		],
	});

	sentryState = {
		browserTracingAttached: false,
		enabled,
		environment,
		feedbackAttached: false,
		piniaAttached: false,
		vueAttached: false,
	};

	return sentryState;
};

export const initStartupSentry = () => {
	const { enabled, environment } = initializeSentryBase();
	return { environment, enabled };
};

export const initSentry = ({
	app,
	router,
	pinia,
}: {
	app: App;
	pinia: Pinia;
	router: Router;
}) => {
	const sentry = initializeSentryBase();

	if (pinia && !sentry.piniaAttached) {
		pinia.use(Sentry.createSentryPiniaPlugin());
		sentry.piniaAttached = true;
	}

	if (sentry.enabled && !sentry.vueAttached) {
		BrowserSentry.addIntegration(Sentry.vueIntegration({ app }));
		sentry.vueAttached = true;
	}

	if (sentry.enabled && !sentry.browserTracingAttached) {
		BrowserSentry.addIntegration(Sentry.browserTracingIntegration({ router }));
		sentry.browserTracingAttached = true;
	}

	if (sentry.enabled && !sentry.feedbackAttached) {
		BrowserSentry.addIntegration(
			Sentry.feedbackIntegration({
				// Additional SDK configuration goes in here, for example:
				colorScheme: "system",
				autoInject: false,
			}),
		);
		sentry.feedbackAttached = true;
	}

	return {
		environment: sentry.environment,
		enabled: sentry.enabled,
	};
};

export function captureException(error: unknown) {
	initializeSentryBase();
	return BrowserSentry.captureException(error);
}
